import { z } from "zod"

import { useCurrentScene } from "@postsunday/remotion-shared/hooks/useCurrentScene"
import { subtitleElementSchema } from "@postsunday/remotion-shared/lib/validations/element"
import { savedInputPropsSchema } from "@postsunday/remotion-shared/lib/validations/input-props"
import { WaitForFonts } from "@postsunday/remotion-shared/providers/waitForFonts"
import { Text } from "@postsunday/remotion-shared/text-styles/text"
import { fitTextElement } from "@postsunday/remotion-shared/text-styles/utils"

import { ClippingComp } from "./clipping"
import { SubtitlesAtChunk } from "./subtitles-at-chunk"

// New component to handle the hook
const SubtitlesScene = ({
  startFromFrame,
  endFrame,
  chunks,
  wordsAtATime,
  el,
  props,
}: {
  startFromFrame: number
  endFrame: number
  chunks: z.infer<
    typeof savedInputPropsSchema
  >["clippingBasedProperties"]["chunks"]
  wordsAtATime: number
  el: z.infer<typeof subtitleElementSchema>
  props: z.infer<typeof savedInputPropsSchema>
}) => {
  const { currentScene } = useCurrentScene({
    chunks,
    startFromFrame,
    endFrame,
    wordsAtATime,
  })
  if (!currentScene.length) return <></>
  return (
    <p
      className="flex size-full"
      style={{
        textAlign: el.style.textAlign,
        fontFamily: el.style.fontFamily,
        textTransform: el.style.casing,
        lineHeight: `${el.style.lineHeightPercent}%`,
        justifyContent:
          el.style.textAlign === "center"
            ? "center"
            : el.style.textAlign === "right"
              ? "flex-end"
              : "flex-start",
      }}
    >
      <span
        style={{
          backgroundColor: el.style.backgroundColor,
          backdropFilter: `${
            "backgroundBlur" in el.style && el.style.backgroundBlur
              ? `blur(${el.style.backgroundBlur}px)`
              : ""
          }`.trim(),
          ...(el.style.layerBlur
            ? {
                filter: `blur(${el.style.layerBlur}px)`,
              }
            : {}),
          // TODO: pass highlightBorderRadius to differentiate
          borderRadius: el.style.borderRadius,
          display: "inline-block",
          paddingTop: el.style.paddingVertical,
          paddingBottom: el.style.paddingVertical,
          paddingLeft: el.style.paddingHorizontal,
          paddingRight: el.style.paddingHorizontal,
        }}
      >
        <SubtitlesAtChunk
          clippingBasedProperties={props.clippingBasedProperties}
          wordsAtATime={el.wordsAtATime}
          animationStyle={el.animationStyle}
          startFromFrame={startFromFrame}
          endFrame={endFrame}
          highlightBackgroundColor={
            el.style.disableHighlight
              ? "transparent"
              : el.style.highlightBackgroundColor
          }
          highlightTextColor={
            (el.style.disableHighlight
              ? el.style.color
              : el.style.highlightTextColor) ?? "#ffffff"
          }
          highlightFontStrokeWidth={el.style.highlightFontStrokeWidth}
          style={{
            color: el.style.color,
          }}
          inactiveColor={el.inactiveColor}
          borderRadius={el.style.borderRadius}
        >
          {(word, allWordsInCurrentChunk) => {
            const {
              color: _1,
              fontSize,
              paddingHorizontal: _2,
              paddingVertical: _3,
              borderRadius: _4,
              backgroundColor: _5,
              fontWeight: inactiveFontWeight,
              ...restStyles
            } = el.style
            return (
              <Text
                content={word}
                style={{
                  fontSize: !el?.disableFitText
                    ? fitTextElement(allWordsInCurrentChunk, el)
                    : fontSize,
                  fontWeight: el.style.highlightFontStrokeWidth
                    ? undefined
                    : inactiveFontWeight,
                  ...restStyles,
                }}
              />
            )
          }}
        </SubtitlesAtChunk>
      </span>
    </p>
  )
}

export const Subtitles = ({
  el,
  props,
}: {
  el: z.infer<typeof subtitleElementSchema>
  props: z.infer<typeof savedInputPropsSchema>
}) => {
  return (
    <WaitForFonts fontFamily={el.style.fontFamily}>
      <ClippingComp {...props} disablePremounting sequenceProps={{}}>
        {({ startFromFrame, endFrame, key }) => {
          return (
            <SubtitlesScene
              startFromFrame={startFromFrame}
              endFrame={endFrame}
              chunks={props.clippingBasedProperties.chunks}
              wordsAtATime={el.wordsAtATime}
              el={el}
              props={props}
              key={key}
            />
          )
        }}
      </ClippingComp>
    </WaitForFonts>
  )
}
